import { widgetHome } from "./widget-home";

/** Widget Studio (/w/:ws/agents/:agentId/widget/:tab?). */
export const widgetStudio = {
  title: "Widget Studio",
  subtitle: "{{agent}} on {{domain}}",
  subtitleNoDomain: "{{agent}} · not installed yet",
  installed: "Installed",
  notInstalled: "Not installed",
  agentNotLive: "Agent not live",
  agentNotLiveHint: {
    draft: "This agent is a draft, so the widget can't load on your site yet. Publish the agent to go live.",
    paused: "This agent is paused, so the widget can't load on your site. Resume the agent to go live.",
  },
  agentGate: {
    title: "Your widget isn't live yet",
    draft: {
      body: "This agent is a draft, so the widget won't load on your site yet. Publish the agent to go live.",
      action: "Publish agent",
      askAdmin: "Ask an admin to publish it.",
      done: "Agent published · the widget now loads on your site",
    },
    paused: {
      body: "This agent is paused, so the widget won't load on your site. Resume the agent to go live.",
      action: "Resume",
      askAdmin: "Ask an admin to resume it.",
      done: "Agent resumed · the widget now loads on your site",
    },
  },
  publishChoice: {
    title: "Publish the widget and the agent?",
    description: "This agent is still a draft, so the widget won't load on your site until the agent is published too.",
    both: "Publish both",
    widgetOnly: "Widget only",
  },
  publishedBoth: "Widget and agent published · the widget now loads on your site",
  publishedAgentDraft: "Widget published, but the agent is still a draft · publish the agent for the widget to load on your site",
  publishedAgentNeedsAdmin: "Widget published, but the agent is still a draft · an admin must publish the agent before the widget loads on your site",
  tabsLabel: "Widget settings",
  tabs: {
    appearance: "Appearance",
    content: "Content",
    home: "Home",
    behavior: "Behavior",
    install: "Install",
    security: "Security",
  },
  saveDraft: "Save draft",
  publish: "Publish",
  draftSaved: "Draft saved · publish to show it to visitors",
  published: "Widget published · visitors see the new look within 1 minute",
  publishedVersion: "Published v{{version}} · {{date}}",
  neverPublished: "Not published yet",
  invalidForm: "Some fields need attention before saving.",
  notFoundTitle: "This agent doesn't exist anymore",
  notFoundBody: "It may have been deleted. Pick another agent from the switcher.",
  appearance: {
    brandColor: "Brand color",
    brandHex: "Hex value",
    pickColor: "Pick brand color",
    onBrand: "Text on brand:",
    onBrandDark: "Dark",
    onBrandLight: "Light",
    onBrandAuto: "(auto, by contrast)",
    ratio: "Contrast {{ratio}}:1",
    aaWarning: "Text on this colour is below the 4.5:1 WCAG AA contrast ratio. It may be hard to read.",
    tooLow: "Neither dark nor white text reaches 3:1 on this colour. Pick a darker or lighter shade.",
    invalidHex: "Use a hex colour like #00aeef",
    presets: "Color presets",
    presetNames: {
      brand: "Sky blue",
      maroon: "Maroon",
      forest: "Forest",
      navy: "Navy",
      amber: "Amber",
      ink: "Ink",
    },
    theme: "Theme",
    themeHint: "Auto follows the visitor's device setting.",
    themeLight: "Light",
    themeDark: "Dark",
    themeAuto: "Auto",
    launcherStyle: "Launcher style",
    launcherIcon: "Bubble icon",
    launcherLabel: "With label",
    launcherLabelText: "Launcher label",
    launcherLabelPlaceholder: "e.g. Ask us",
    position: "Launcher position",
    positionLeft: "Bottom left",
    positionRight: "Bottom right",
    radius: "Corner radius",
    radiusSquare: "Square",
    radiusRound: "Round",
    font: "Font",
    fontHint: "The typeface of the launcher and chat panel on your site.",
    fontWebsite: "Match your website",
    fontWebsiteHint: "Uses the font your page already loads. Nothing is downloaded.",
    fontGoogleHint: "Loads {{font}} from Google Fonts on your page. If your site blocks Google Fonts, visitors see a clean system font instead.",
    fontDefaultHint: "The font the widget ships with. Nothing is downloaded.",
    fontSample: "Aa Sample answer text",
    headerTitle: "Header title",
    headerSubtitle: "Header subtitle",
    avatar: "Avatar",
    upload: "Upload",
    uploading: "Uploading…",
    useAgentAvatar: "Use agent avatar",
    avatarHint: "PNG, JPEG or WebP · max 2 MB",
    avatarUploaded: "{{name}} uploaded",
    avatarReset: "Using the agent avatar",
    avatarInvalid: "Use a PNG, JPEG or WebP image up to 2 MB.",
    avatarFailed: "The image couldn't be uploaded. Try again.",
    avatarAlt: "Widget avatar",
    iconUpload: {
      title: "Launcher icon",
      hint: "PNG, JPEG or WebP · max 2 MB · square images work best",
      upload: "Upload launcher icon",
      remove: "Remove icon",
      previewLabel: "Launcher icon preview",
      usingCustom: "Using your uploaded icon.",
      usingDefault: "Using the default chat bubble.",
      checking: "Checking {{name}}…",
      uploading: "Uploading {{name}}…",
      ready: "{{name}} is ready. Save your changes to apply it.",
      removed: "Icon removed. Save your changes to go back to the chat bubble.",
      retry: "Try again",
      wrongType: "{{name}} isn't a PNG, JPEG or WebP image. SVG and other formats aren't supported.",
      tooBig: "{{name}} is {{size}} MB. Use an image up to 2 MB.",
      empty: "{{name}} is empty. Choose a different image.",
      tooSmall: "{{name}} is {{width}} × {{height}} px. Use an image at least {{min}} × {{min}} px.",
      tooLarge: "{{name}} is {{width}} × {{height}} px. Use an image up to {{max}} × {{max}} px.",
      unreadable: "We couldn't read {{name}}. It may be damaged. Choose a different image.",
      failed: "{{name}} couldn't be uploaded. Check your connection and try again.",
    },
    branding: "Show “Powered by {{product}}”",
    brandingLocked: "Remove on Pro.",
    brandingIncluded: "Your plan lets you hide it.",
    comparePlans: "Compare plans",
    brandingFeature: "Removing “Powered by {{product}}”",
    brandingSwitch: "Powered by {{product}}",
    brandingSwitchLocked: "Powered by {{product}} (upgrade to remove)",
  },
  content: {
    welcome: "Welcome message",
    starters: "Starter questions",
    startersMax: "Up to {{count}}",
    starterPlaceholder: "Starter question {{n}}",
    starterTip: "Tip: your top asked questions are in",
    analytics: "Analytics",
    placeholder: "Input placeholder",
    privacyNotice: "Privacy notice",
    privacyUrl: "Privacy notice link",
    privacyUrlInvalid: "Use a full https:// link",
    charLimit: "Character limit",
    charLimitRange: "Between {{min}} and {{max}}",
    attachments: "Attachments",
    attachmentsOff: "Off",
    attachmentsImages: "Images only",
    attachmentsImagesPdf: "Images + PDF",
  },
  behavior: {
    autoOpen: "Open automatically",
    autoOpenHint: "Only on the first visit, desktop only.",
    after: "After",
    seconds: "seconds",
    autoOpenDelay: "Seconds before opening",
    proactive: "Proactive message bubble",
    proactiveHint: "A short nudge above the launcher. Close the preview widget to see it.",
    proactiveMessage: "Message",
    proactiveDelay: "Show after (seconds)",
    proactivePages: "Show on pages matching",
    proactivePagesHint: "Comma-separated paths, e.g. /pricing, /contact/*. Empty = every page.",
    showOnMobile: "Show on mobile",
    leadCapture: "Collect lead before chat",
    leadOff: "Off",
    leadOptional: "Optional",
    leadRequired: "Required",
    leadHintOff: "Visitors start chatting right away.",
    leadHintOptional: "Optional asks for name and mobile but lets visitors skip.",
    leadHintRequired: "Visitors share their name and mobile before the first message.",
    outOfHours: "Outside office hours",
    outOfHoursCallback: "Keep answering · offer callback instead of live handoff",
    outOfHoursHide: "Keep answering · hide “Talk to staff”",
    outOfHoursOffline: "Show offline message only",
    sound: "Notification sound",
    soundHint: "Soft chime on new replies",
    remember: "Remember conversation",
    rememberHint: "Visitors return to their last chat on the same browser",
    rememberDays: "Days to remember",
  },
  install: {
    detected: "Detected on {{origin}}",
    lastSeen: "Last seen {{when}} · {{count}} site(s)",
    notDetected: "Not detected yet",
    notDetectedHint: "Add the snippet to your site, then re-check. It can take a minute after the first page load.",
    recheck: "Re-check",
    rechecked: "Install status refreshed",
    methods: "Install method",
    html: "HTML",
    react: "React / Next.js",
    wordpress: "WordPress",
    portal: "Portal / LMS",
    identity: "Signed-in visitors",
    mobile: "Mobile app",
    iframe: "Iframe",
    iframeIntro: "Show the chat inside your own page. The frame opens your launch endpoint, which signs the customer in and sends them to the chat, so they never see a sign-in page.",
    iframeAllowed_one: "{{count}} site may show the chat",
    iframeAllowed_other: "{{count}} sites may show the chat",
    iframeManage: "Manage allowed sites",
    iframeNotes: {
      title: "Before you paste it",
      src: "Point src at the endpoint on your own site that asks Sagot for a launch link (mode embed, your page's address as parent_origin) and answers 303 to it. /sagot/launch is a placeholder until you register your mint URL.",
      csp: "If your site sets a Content-Security-Policy, add frame-src {{origin}}.",
      keep: "Keep the frame off payment and deposit pages.",
      reload: "A reload, or a session that ended, returns to your endpoint and carries on signed in.",
    },
    iframeNeedsTitle: "Show the chat inside your own page",
    iframeNeedsBody: "The Iframe method appears once a site of yours is allowed to frame the chat. Add the site's exact address, such as https://www.example.com, under Settings > Secure launch.",
    iframeNeedsAction: "Add an allowed site",
    htmlHint: "Paste before </body> on every page.",
    reactHint: "Paste this component into your app and render it once in your root layout. It loads the same script, so there is nothing to install. Pass signedIn and name for signed-in users; it signs them out of the assistant when they sign out.",
    agentId: "Agent ID",
    snippetLabel: "{{method}} snippet",
    guide: "Identity verification guide",
    emailDev: "Email instructions to your developer",
    snippetsError: "Install snippets couldn't load.",
    identityIntro: "Let people who are signed in to your site chat as themselves, so they read what their account may read. Your server signs a short-lived token; the widget fetches it for you.",
    identityClient: "On your page",
    identityServer: "On your server",
    identityServerHint: "Add an endpoint at /sagot-token that answers only for visitors who are signed in to your site.",
    identityLang: "Server language",
    identityLangs: { node: "Node.js", php: "PHP", python: "Python" },
    identityNotes: {
      title: "How it works",
      secret: "The secret key stays in your server environment (SAGOT_IDENTITY_SECRET). Never put it in the page or in code that visitors can read.",
      ttl: "Tokens last 10 minutes. The longest a token may live is 15 minutes.",
      refresh: "The widget fetches a token when it loads, refreshes it a minute before it expires, and once more if the server answers 401.",
      logout: "Call logout when the visitor signs out of your site: it forgets the visitor and starts the next chat as a guest.",
    },
    mobileIntro: "Add the assistant to your own iOS or Android app. There is nothing to download from us: your app opens the assistant's chat page in a WebView, and you ship it with your app.",
    mobileFramework: "Framework",
    mobileFrameworks: { react_native: "React Native", flutter: "Flutter", ios: "iOS (Swift)", android: "Android (Kotlin)", maui: ".NET MAUI", capacitor: "Capacitor / Ionic", ios_launch: "iOS signed-in (Swift)", android_launch: "Android signed-in (Kotlin)" },
    mobileNotes: {
      title: "Good to know",
      channel: "The chat page is served by the Web app channel. Keep it switched on in Channels.",
      same: "It is the same assistant: answers with sources, handoff to your team and conversations in your Inbox work as on your website.",
      updates: "Change the assistant's knowledge in Sagot and the app answers differently straight away. You do not need to publish a new app version.",
      guests: "Visitors chat as guests and can confirm their email with a code inside the chat. Signed-in identity tokens are for the script widget on websites.",
      launch: {
        backend: "The signed-in tabs open the chat in the system browser view (Safari view or Custom Tabs), never in a WebView or an iframe. The app asks your own backend for the link; the Sagot key stays on your server.",
        browser: "Your backend calls POST /api/v1/launch-sessions and returns launch_url. Open it at once: the one-time code after the # is used a single time.",
        push: "Send the customer's context with POST /api/v1/customers/context, or subscribe to message.created: the event carries customer_ref, so your backend can match a reply to the signed-in customer.",
      },
    },
  },
  emailDialog: {
    title: "Email install instructions",
    email: "Developer email",
    emailInvalid: "Enter a valid email address",
    note: "Note (optional)",
    includes: "Includes the snippet, the Agent ID and a link to the identity verification guide. The secret key is not included.",
    send: "Send",
    sent: "Instructions sent to {{email}}",
  },
  security: {
    allowedDomains: "Allowed domains",
    allowedDomainsHint: "The widget only loads on these domains (subpaths included). example.com also covers www.example.com; use *.example.com for every subdomain.",
    domainPlaceholder: "e.g. help.example.com",
    localHint: "Testing locally? Add localhost:3000 (include the port).",
    add: "Add",
    remove: "Remove {{domain}}",
    domainInvalid: "Enter a domain like help.example.com, *.example.com or localhost:3000.",
    domainDuplicate: "{{domain}} is already in the list.",
    domainsFull: "You can add up to {{max}} domains. Remove one to add another.",
    domainsCount: "{{count}} of {{max}}",
    domainAdded: "{{domain}} added",
    domainRemoved: "{{domain}} removed",
    noDomains: "No domains yet — the widget won't load anywhere until you add one.",
    identity: "Identity verification",
    identityHint: "Required for member-only answers and actions that need to know who is asking.",
    step1: "When a user signs in to your site, your server signs a JWT (HS256) with their user ID and role using the secret below.",
    step2: "Your page passes it to {{product}}('identify', { token }).",
    step3: "{{product}} verifies the signature — users can't fake who they are.",
    secret: "Secret key",
    secretConfigured: "A secret key is set. It is shown only once, when generated.",
    secretMissing: "No secret key yet. Generate one to start verifying users.",
    secretHint: "Keep this on your server only.",
    previousExpires: "The previous key keeps working until {{date}}.",
    rotate: "Rotate key",
    generate: "Generate key",
    sample: "Signing example",
    rateVisitor: "Messages per visitor / hour",
    rateIp: "New chats per IP / day",
    rateVisitorHint: "Enforced on every message. Over the limit, the visitor is asked to wait a little.",
    rateIpHint: "Counts conversations started from one network. Schools and offices share one connection, so allow for everyone on it.",
    blocked: "Blocked IPs",
    blockedHint: "One IP or CIDR range per line. Text after # is a note.",
    blockedInvalid: "Line {{line}} isn't an IP address or CIDR range",
    save: "Save security settings",
    saved: "Security settings saved",
  },
  rotateDialog: {
    title: "Rotate secret key?",
    titleGenerate: "Generate secret key?",
    warning: "The current key keeps working for 24 hours so you can update your server. After that, tokens signed with the old key are rejected.",
    confirm: "Rotate key",
    confirmGenerate: "Generate key",
    rotated: "New key generated · old key expires in 24 hours",
    newTitle: "Your new secret key",
    newBody: "Copy it now and store it on your server. For your security it won't be shown again.",
    secretLabel: "New secret key",
    done: "I've stored it",
  },
  preview: {
    live: "Live preview",
    liveHint: "· changes are not published until you save",
    device: "Preview device",
    desktop: "Desktop",
    mobile: "Mobile",
    openDemo: "Open demo",
    openDemoLabel: "Open demo in a new tab",
    footer: "Preview uses sample answers. Try the real assistant in the",
    playground: "Playground",
    conversation: "Sample conversation",
    region: "Widget preview",
    siteName: "Your website",
    siteHeadline: "Your page content appears here",
    siteBody: "The widget floats above your site exactly like this.",
    sampleQuestion: "What are your office hours?",
    sampleAnswer: "Answers come from your knowledge sources, with citations — try a real question in the Playground.",
    sampleSource: "Your knowledge source",
    toggle: "Toggle preview widget",
    close: "Close preview widget",
    poweredBy: "Powered by {{product}}",
    send: "Send",
    defaultTitle: "Chat with us",
  },
  home: widgetHome,
} as const;
